import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Step,
  StepLabel,
  Stepper,
  Typography,
  Modal,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import api from "api";
import { BUILD, REGRESSION, UAT, PREPROD, PROD } from "constants/Constants";
import LoadingBackdrop from "components/LoadingBackDrop";

const steps = [
  {
    label: BUILD,
    targetKey: "targetBuildDate",
    actualKey: "releaseSignOffDate",
  },
  {
    label: REGRESSION,
    targetKey: "targetRegressionDate",
    actualKey: "actualRegressionDate",
  },
  { label: UAT, targetKey: "targetUatDate", actualKey: "actualUatDate" },
  {
    label: PREPROD,
    targetKey: "targetPreProdDate",
    actualKey: "actualPreProdDate",
  },
  { label: PROD, targetKey: "targetProdDate", actualKey: "actualProdDate" },
];

function ReleaseSignOff({
  releaseData,
  enableChangeRequired,
  readyForBuild,
  reloadParent,
}) {
  const [waitingForAPI, setWaitingForAPI] = useState(false);
  const [displayMessage, setDisplayMessage] = useState(null);
  const [activeStep, setActiveStep] = useState(0);
  const [openSignOff, setOpenSignOff] = useState(false);
  const [justification, setJustification] = useState("");
  const [approvals, setApprovals] = useState([]);
  const [completedSteps, setCompletedSteps] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [loadingMessage, setLoadingMessage] = useState(
    "Loading Release Approvals..."
  );
  const [editingIndex, setEditingIndex] = useState(null);
  const [updatedTargetDates, setUpdatedTargetDates] = useState({});
  const [tempDate, setTempDate] = useState(null);

  const handleDisplayMessageClose = () => {
    setDisplayMessage(null);
  };

  useEffect(() => {
    fetchReleaseApprovals();
  }, [releaseData]);

  const fetchReleaseApprovals = async () => {
    try {
      const data = releaseData?.allReleaseApprovals || [];
      setApprovals(data);
      setIsLoading(false);

      let completed = {};
      let sequentialComplete = true;

      for (let i = 0; i < steps.length; i++) {
        const step = steps[i];
        const approval = data.find((a) => a.pipelineStatusText === step.label);

        if (
          !approval ||
          !approval.releaseSignOffText ||
          !approval.releaseSignOffDate
        ) {
          sequentialComplete = false;
        }

        if (sequentialComplete) {
          completed[i] = true;
        } else {
          break;
        }
      }

      setCompletedSteps(completed);
      let lastCompletedStep = Object.keys(completed).length;
      setActiveStep(
        lastCompletedStep < steps.length ? lastCompletedStep : steps.length
      );
    } catch (error) {
      console.error("Error fetching release approvals:", error);
      setIsLoading(false);
    }
  };

  // Return nothing (null) if releaseData is not ready
  if (!releaseData || releaseData.length === 0) {
    return null; // or return <Loading /> if you want a spinner
  }

  const handleTargetDateClick = (index, targetDate) => {
    if (!completedSteps[index]) {
      setEditingIndex(index);
      setTempDate(targetDate ? dayjs(targetDate) : null);
    }
  };

  const handleTargetDateChange = async (
    index,
    newValue,
    stepLabel,
    approvalKey
  ) => {
    setEditingIndex(null);
    setUpdatedTargetDates((prev) => ({
      ...prev,
      [index]: newValue ? newValue.format("MM/DD/YYYY") : "",
    }));
    const step = steps.find((s) => s.label === stepLabel);
    if (!step?.targetKey) return;
    if (newValue) {
      const parsedDate = dayjs(newValue);

      if (!parsedDate.isValid()) {
        alert("Please enter a valid date in MM/DD/YYYY format.");
        return;
      }
      try {
        setWaitingForAPI(true);
        setLoadingMessage("Setting target date ...");
        const updatedData = {
          releaseScheduleKey: releaseData?.releaseScheduleKey,
          pipelineStatusText: stepLabel,
          releaseTargetDate: parsedDate.format("YYYY-MM-DD"),
        };

        if (approvalKey) {
          const updatedApproval = await api.ReleaseApproval.updateTargetDate(
            approvalKey,
            updatedData
          );
          setApprovals((prevApprovals) =>
            prevApprovals.map((a) =>
              a.pipelineStatusText === stepLabel
                ? { ...a, ...updatedApproval.data }
                : a
            )
          );
        } else {
          await api.ReleaseApproval.createReleaseApproval(updatedData).then(
            (newApproval) => {
              setApprovals((prevApprovals) => [
                ...prevApprovals,
                newApproval.data,
              ]);
            }
          );
        }
        setDisplayMessage({
          message: "Updated Target Date for " + stepLabel,
          title: "Success",
        });
      } catch (error) {
        setDisplayMessage({
          message:
            error.response?.data?.errors ||
            "Error updating Release With Target Date",
          title: "Error",
        });
      } finally {
        setWaitingForAPI(false);
        setLoadingMessage("");
      }
    }
  };

  const handleChangesRequired = async (stepIndex) => {
    const stepLabel = steps[stepIndex].label;
    const approval = approvals.find((a) => a.pipelineStatusText === stepLabel);

    if (!approval) return;

    let updatedFields = {};
    let message = `Cleared Actual ${stepLabel} Date`;

    if (stepLabel === BUILD) {
      updatedFields = {
        releaseSignOffDate: null, // Clear Build actual date
        revertToStep: stepLabel,
      };
      setLoadingMessage("Clearing Actual Build & Regression Dates ...");
      message = "Cleared Actual Build & Regression Dates";
    } else if (stepLabel === REGRESSION) {
      updatedFields = {
        releaseSignOffDate: null, // Clear Regression actual date
        revertToStep: stepLabel,
      };
      setLoadingMessage("Clearing Actual Regression Dates ...");
    }

    try {
      setWaitingForAPI(true);

      // If clearing Build date, also clear Regression
      if (stepLabel === BUILD) {
        const regressionApproval = approvals.find(
          (a) => a.pipelineStatusText === REGRESSION
        );
        if (regressionApproval) {
          await api.ReleaseApproval.updateReleaseApproval(
            regressionApproval.releaseApprovalKey,
            {
              releaseSignOffDate: null, // Clear Regression date as well
            }
          );
        }
      }

      await api.ReleaseApproval.updateReleaseApproval(
        approval.releaseApprovalKey,
        updatedFields
      );
      setDisplayMessage({ message, title: "Success" });
      reloadParent();
    } catch (error) {
      setDisplayMessage({
        message: error.response?.data?.errors || "Error clearing actual date",
        title: "Error",
      });
    } finally {
      setWaitingForAPI(false);
      setLoadingMessage("");
    }
  };

  const handleNext = async () => {
    setOpenSignOff(false);
    if (activeStep < steps.length - 1) {
      const stepLabel = steps[activeStep].label;
      const approval = approvals.find(
        (a) => a.pipelineStatusText === stepLabel
      );
      const releaseSignOffDate = dayjs().format("YYYY-MM-DD"); // Capture current date

      const postData = {
        releaseScheduleKey: releaseData?.releaseScheduleKey, // Always needed
        pipelineStatusText: stepLabel,
        releaseSignOffText: justification,
        releaseSignOffDate: releaseSignOffDate,
      };

      try {
        setWaitingForAPI(true);

        if (stepLabel === BUILD) {
          setLoadingMessage(
            "Generating sheets. It might take a while. Please wait ..."
          );
        } else if (stepLabel === REGRESSION) {
          setLoadingMessage("Processing Regression ...");
        } else if (stepLabel === UAT) {
          setLoadingMessage("Processing UAT ...");
        } else if (stepLabel === PREPROD) {
          setLoadingMessage("Processing PreProd ...");
        } else if (stepLabel === PROD) {
          setLoadingMessage("Processing Prod ...");
        }
        if (approval) {
          // If step exists, update via PUT
          const updatedApproval =
            await api.ReleaseApproval.updateReleaseApproval(
              approval.releaseApprovalKey,
              postData
            );

          setApprovals((prevApprovals) =>
            prevApprovals.map((a) =>
              a.pipelineStatusText === stepLabel
                ? { ...a, ...updatedApproval.data }
                : a
            )
          );
          setDisplayMessage({
            message: `Updated ${stepLabel} Release Sign off`,
            title: "Success",
          });
        } else {
          // Create a new approval via POST and ensure it fully resolves
          await api.ReleaseApproval.createReleaseApproval(postData).then(
            (newApproval) => {
              setApprovals((prevApprovals) => [
                ...prevApprovals,
                newApproval.data,
              ]);
            }
          );
          setDisplayMessage({
            message: "Created Release Sign off for " + stepLabel,
            title: "Success",
          });
        }
        // Update UI state
        setCompletedSteps((prev) => ({ ...prev, [activeStep]: true }));
        setActiveStep((prevActiveStep) => prevActiveStep + 1);
      } catch (error) {
        setDisplayMessage({
          message:
            error.response?.data?.errors || "Error updating Release Sign off",
          title: "Error",
        });
      } finally {
        setLoadingMessage("");
        setWaitingForAPI(false);
      }
    }
    setJustification("");
  };

  const handleOpenModal = () => {
    const currentStepDetails = getStepDetails(steps[activeStep].label);
    setJustification(currentStepDetails.signOffText || "");
    setOpenSignOff(true);
  };

  const handleCloseModal = () => {
    setOpenSignOff(false);
  };

  const getStepDetails = (stepLabel) => {
    const approval = approvals.find((a) => a.pipelineStatusText === stepLabel);
    const targetKey = steps.find((s) => s.label === stepLabel)?.targetKey;
    const actualKey = steps.find((s) => s.label === stepLabel)?.actualKey;
    const formatDate = (timestamp) =>
      timestamp ? dayjs(timestamp).format("MM/DD/YYYY") : "N/A";
    const targetDate = approval?.releaseTargetDate
      ? formatDate(approval.releaseTargetDate)
      : formatDate(releaseData[targetKey]) || "N/A";
    const actualDate = approval?.releaseSignOffDate
      ? formatDate(approval.releaseSignOffDate)
      : formatDate(releaseData[actualKey]) || "N/A";

    return {
      approvalKey: approval?.releaseApprovalKey,
      targetDate,
      actualDate,
      signOffText: approval?.releaseSignOffText || "",
    };
  };

  if (isLoading) {
    return (
      <Typography sx={{ mt: 2, fontWeight: "bold" }}>
        Loading release approvals...
      </Typography>
    );
  }

  return (
    <Box sx={{ width: "80%", mt: 1, ml: 5 }}>
      {waitingForAPI && <LoadingBackdrop message={loadingMessage} />}
      {displayMessage && (
        <Dialog open onClose={handleDisplayMessageClose}>
          <DialogTitle>{displayMessage.title}</DialogTitle>
          <DialogContent>{displayMessage.message}</DialogContent>
          <DialogActions>
            <Button onClick={handleDisplayMessageClose}>Close</Button>
          </DialogActions>
        </Dialog>
      )}
      <Stepper activeStep={activeStep} orientation="vertical">
        {steps.map((step, index) => {
          const { approvalKey, targetDate, actualDate, signOffText } =
            getStepDetails(step.label);
          const showChangesRequired =
            (step.label === BUILD || step.label === REGRESSION) &&
            actualDate !== "N/A" &&
            enableChangeRequired;

          return (
            <Step
              key={step.label}
              completed={!!completedSteps[index]}
              sx={{ mb: -1 }}
            >
              <StepLabel>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      width: "100%",
                    }}
                  >
                    <Typography sx={{ fontWeight: "bold" }}>
                      {step.label}
                    </Typography>

                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: "normal", color: "gray" }}
                      >
                        Target Date:
                      </Typography>
                      {editingIndex === index ? (
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          <DatePicker
                            format="MM/DD/YYYY"
                            value={tempDate}
                            onChange={(newValue) => setTempDate(newValue)}
                            slotProps={{
                              textField: { variant: "standard", size: "small" },
                            }}
                          />
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() => {
                              handleTargetDateChange(
                                index,
                                tempDate,
                                step.label,
                                approvalKey
                              );
                              setEditingIndex(null);
                            }}
                          >
                            OK
                          </Button>
                          <Button
                            variant="text"
                            size="small"
                            onClick={() => {
                              setEditingIndex(null);
                              setTempDate(null);
                            }}
                          >
                            Cancel
                          </Button>
                        </Box>
                      ) : (
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: "normal",
                            color: "gray",
                            cursor: "pointer",
                          }}
                          onClick={() =>
                            handleTargetDateClick(index, targetDate)
                          }
                        >
                          {targetDate}
                        </Typography>
                      )}
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: "normal", color: "gray" }}
                      >
                        {" | "} Actual Date:{" "}
                        {completedSteps[index] ? actualDate : "N/A"}
                      </Typography>
                    </Box>
                  </Box>
                </LocalizationProvider>
                {signOffText && (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 0.5 }}
                  >
                    {signOffText}
                  </Typography>
                )}
              </StepLabel>

              {/* "Changes Required" Button */}
              {showChangesRequired && (
                <Box sx={{ mb: 0, mt: 1 }}>
                  <Button
                    variant="outlined"
                    color="error"
                    onClick={() => handleChangesRequired(index)}
                  >
                    Changes Required
                  </Button>
                </Box>
              )}

              {activeStep === index && (
                <Box sx={{ mb: 0 }}>
                  <Button
                    variant="contained"
                    onClick={handleOpenModal}
                    disabled={!readyForBuild}
                  >
                    Proceed
                  </Button>
                </Box>
              )}
            </Step>
          );
        })}
      </Stepper>

      {activeStep === steps.length && (
        <Typography sx={{ mt: 2, fontWeight: "bold", color: "green" }}>
          All steps completed successfully!
        </Typography>
      )}

      {/* Modal for Sign-off */}
      <Modal open={openSignOff} onClose={handleCloseModal}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 450,
            bgcolor: "background.paper",
            boxShadow: 24,
            p: 3,
            borderRadius: 2,
          }}
        >
          <Typography variant="h6" sx={{ mb: 2 }}>
            Release Sign-off
          </Typography>
          <Typography variant="body1" sx={{ mb: 2 }}>
            In order to progress the pipeline, confirmation of successful
            completion must be provided.
          </Typography>
          <TextField
            label="Justification"
            multiline
            rows={3}
            fullWidth
            value={justification}
            onChange={(e) => setJustification(e.target.value)}
          />
          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
            <Button onClick={handleCloseModal} sx={{ mr: 2 }}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleNext}
              disabled={!justification.trim()} // Disable if justification is empty
            >
              Confirm & Continue
            </Button>
          </Box>
        </Box>
      </Modal>
    </Box>
  );
}

export default ReleaseSignOff;
